import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { ITask2ReportItem } from '../../../fi-task-v2-report';
import { FiTaskAssignmentType, FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { FiTaskV2ReportHelperService } from '@fi-app/task-v2/fi-task-v2-report/services/fi-task-v2-report-helper.service';
import { Subject } from 'rxjs';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { ITaskV2Base } from '@fi-app/task-v2/fi-task-v2';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatusesConfig } from '@fi-app/task-v2/fi-task-v2-consts';

export interface ITaskActionExecutedData {
    type: ACTIONS_TYPES;
    item: ITask2ReportItem;
}

export enum ACTIONS_TYPES {
    EDIT = 'edit',
    OPEN = 'open',
    UPDATE_STATUS = 'update_status',
    SEND_REMINDER = 'send_reminder',
    ARCHIVE = 'archive',
    RESTORE = 'restore',
}

export interface ITaskActionBuilderContext {
    cdr: ChangeDetectorRef;
    // taskStatusUpdated$: Subject<ITask2ReportItem>;
    actionExecuted$: Subject<ITaskActionExecutedData>;
}

@Injectable()
export class TaskActionsBuilderService {
    private readonly appTextService = inject(AppTextService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiUserDataService = inject(FiUserDataService);
    private readonly accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private readonly fiTaskV2Service = inject(FiTaskV2Service);
    private readonly fiTaskHelperService = inject(FiTaskHelperService);
    private readonly fiTaskV2HelperService = inject(FiTaskV2HelperService);
    private readonly fiTaskV2ReportHelperService = inject(FiTaskV2ReportHelperService);
    private readonly fiTaskSyncService = inject(FiTaskSyncService);

    createTaskActions(
        context: ITaskActionBuilderContext,
    ): ReadonlyArray<Readonly<IItemsListAction2<ITask2ReportItem>>> {
        return [
            this.createOpenChecklistAction(),
            this.createOpenAuditChecklistAction(),
            this.createEditAction(),
            this.createOpenAction(),
            this.createUpdateStatusAction(context),
            this.createSendReminderAction(context),
            this.createArchiveAction(context, true),
            this.createRestoreAction(context),
        ];
    }

    private isChecklistTask(item: ITask2ReportItem): boolean {
        return [FiTaskType.ChecklistAutomated, FiTaskType.ChecklistOneTime].includes(item.TaskType);
    }

    private createOpenChecklistAction(): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('checklist.view_checklist'),
            condition: (item) => {
                return (
                    this.isChecklistTask(item) &&
                    this.fiTaskV2HelperService.canOpenTaskChecklistView(item.AdditionalData.ChecklistData) &&
                    !item.AdditionalData.ChecklistData.IsChecklistAudit
                );
            },
            onClick: (item) => {
                this.fiTaskV2ReportHelperService.setCurrentTaskItem(undefined);
                this.fiTaskV2HelperService.openTaskChecklistView(item.AdditionalData.ChecklistData);
            },
            iconClass: 'fa-regular fa-ballot-check',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createOpenAuditChecklistAction(): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('checklist.view_audit'),
            condition: (item) => {
                return (
                    this.isChecklistTask(item) &&
                    this.fiTaskV2HelperService.canOpenTaskChecklistView(item.AdditionalData.ChecklistData) &&
                    item.AdditionalData.ChecklistData.IsChecklistAudit
                );
            },
            onClick: (item) => {
                this.fiTaskV2ReportHelperService.setCurrentTaskItem(undefined);
                this.fiTaskV2HelperService.openTaskChecklistView(item.AdditionalData.ChecklistData);
            },
            iconClass: 'fa-regular fa-person-chalkboard',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createEditAction(): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.edit_task'),
            condition: (item) => {
                return (
                    this.isCurrentUserTaskCreator(item) &&
                    item.TaskType !== FiTaskType.ChecklistAutomated &&
                    !item.IsArchived
                );
            },
            onClick: (item) => {
                this.fiTaskV2ReportHelperService.setCurrentTaskItem(undefined);
                this.fiStateService.registerReturnToCurrentState(FiStateNames.TaskV2.Edit);
                this.fiStateService.go(FiStateNames.TaskV2.Edit, { id: item.Id });
            },
            iconClass: 'fa-regular fa-pen-to-square',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createOpenAction(): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.open_task'),
            condition: (item) => {
                return this.isCurrentUserTaskCreator(item) || item.HasViewAccess;
            },
            onClick: (item) => {
                this.fiTaskV2ReportHelperService.setCurrentTaskItem(undefined);
                this.fiStateService.registerReturnToCurrentState(FiStateNames.TaskV2.Details);
                this.fiStateService.go(FiStateNames.TaskV2.Details, { id: item.Id });
            },
            iconClass: 'fa-regular fa-clipboard-check',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createUpdateStatusAction(context: ITaskActionBuilderContext): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.update_status'),
            condition: (item) => {
                return (this.isCurrentUserTaskCreator(item) || item.HasViewAccess) && !item.IsArchived;
            },
            onClick: () => {
                // ignored - handled by subActions
            },
            iconClass: 'fa-regular fa-clipboard',
            optionType: FiItemsListOptionType.Icon,
            subActions: this.createUpdateStatusSubActions(context),
        };
    }

    private createSendReminderAction(context: ITaskActionBuilderContext): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.send_reminder'),
            condition: (item) => {
                return this.canSendRemainder(item, true);
            },
            onClick: (item) => {
                this.fiTaskV2Service.sendReminder(item.Id).then(() => {
                    this.fiTaskV2HelperService.showReminderSentSnackbar();
                    context.actionExecuted$.next({ type: ACTIONS_TYPES.SEND_REMINDER, item: item });
                });
            },
            iconClass: 'fa-regular fa-bell-ring',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    canSendRemainder(item: ITaskV2Base, isFromReport: boolean): boolean {
        let result: boolean =
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Local,
                AccessRightType.Share,
            ) &&
            item.AssignedUsers.length > 0 &&
            !item.IsArchived &&
            [FiTaskStatusType.NotStarted, FiTaskStatusType.InProcess].includes(item.Status) &&
            item.AssignmentType !== FiTaskAssignmentType.Private;
        if (isFromReport) {
            result = result && item.HasEditAccess;
        } else {
            result = result && this.isCurrentUserTaskCreator(item);
        }
        return result;
    }

    canArchiveTask(item: ITaskV2Base, isFromReports: boolean): boolean {
        // https://chainformation.atlassian.net/browse/FI-6520?focusedCommentId=73758
        // Only the creator can archive their task, but this can be done on all statuses.
        if (/*![FiTaskStatusType.Completed, FiTaskStatusType.Cancelled].includes(item.Status) || */ item.IsArchived) {
            return false;
        }
        if (this.fiTaskHelperService.isAutomatedTask(item.TaskType)) {
            // special case for automated tasks
            return isFromReports && item.HasEditAccess;
        }
        return this.isCurrentUserTaskCreator(item);
    }

    canRestoreDeleteTask(item: ITaskV2Base): boolean {
        if (!item.IsArchived) {
            return false;
        }
        if (this.fiTaskHelperService.isAutomatedTask(item.TaskType)) {
            // special case for automated tasks
            return item.ArchivedByUserId == this.fiUserDataService.getUserId() || item.HasEditAccess;
        }
        return this.isCurrentUserTaskCreator(item);
    }

    private createArchiveAction(
        context: ITaskActionBuilderContext,
        isFromReports: boolean,
    ): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.archive_task'),
            condition: (item) => {
                return this.canArchiveTask(item, isFromReports);
            },
            onClick: (item) => {
                this.archiveTask(item, context.cdr, context.actionExecuted$);
            },
            iconClass: 'fa-regular fa-box-archive',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createRestoreAction(context: ITaskActionBuilderContext): IItemsListAction2<ITask2ReportItem> {
        return {
            title: this.appTextService.get('task.restore_task'),
            condition: (item) => {
                return this.canRestoreDeleteTask(item);
            },
            onClick: (item) => {
                this.restoreTask(item, context.cdr, context.actionExecuted$);
            },
            iconClass: 'fa-regular fa-arrow-rotate-left',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private createUpdateStatusSubActions(context: ITaskActionBuilderContext): IItemsListAction2<ITask2ReportItem>[] {
        return [
            {
                title: this.appTextService.get(FiTaskStatusesConfig[FiTaskStatusType.NotStarted].textId),
                condition: (item) => {
                    return (this.isCurrentUserTaskCreator(item) || item.HasViewAccess) && !item.IsArchived;
                },
                onClick: (item) => {
                    this.updateStatus(item, FiTaskStatusType.NotStarted, context.cdr, context.actionExecuted$);
                },
                iconClass: 'far fa-clipboard regular',
                isButtonSelected: (item) => item.Status === FiTaskStatusType.NotStarted,
                optionType: FiItemsListOptionType.Icon,
            },
            {
                title: this.appTextService.get(FiTaskStatusesConfig[FiTaskStatusType.InProcess].textId),
                condition: (item) => {
                    return (this.isCurrentUserTaskCreator(item) || item.HasViewAccess) && !item.IsArchived;
                },
                onClick: (item) => {
                    this.updateStatus(item, FiTaskStatusType.InProcess, context.cdr, context.actionExecuted$);
                },
                isButtonSelected: (item) => item.Status === FiTaskStatusType.InProcess,
                iconClass: 'far fa-hourglass-half blue',
                optionType: FiItemsListOptionType.Icon,
            },
            {
                title: this.appTextService.get(FiTaskStatusesConfig[FiTaskStatusType.Completed].textId),
                condition: (item) => {
                    return (this.isCurrentUserTaskCreator(item) || item.HasViewAccess) && !item.IsArchived;
                },
                onClick: (item) => {
                    this.updateStatus(item, FiTaskStatusType.Completed, context.cdr, context.actionExecuted$);
                },
                isButtonSelected: (item) => item.Status === FiTaskStatusType.Completed,
                iconClass: 'far fa-clipboard-check green',
                optionType: FiItemsListOptionType.Icon,
            },
            {
                title: this.appTextService.get(FiTaskStatusesConfig[FiTaskStatusType.Cancelled].textId),
                condition: (item) => {
                    return this.isCurrentUserTaskCreator(item) && !item.IsArchived;
                },
                onClick: (item) => {
                    this.updateStatus(item, FiTaskStatusType.Cancelled, context.cdr, context.actionExecuted$);
                },
                isButtonSelected: (item) => item.Status === FiTaskStatusType.Cancelled,
                iconClass: 'far fa-circle-x red',
                optionType: FiItemsListOptionType.Icon,
            },
        ];
    }

    private isCurrentUserTaskCreator(item: ITaskV2Base): boolean {
        return item.CreatedByUserId === this.fiUserDataService.getUserId();
    }

    private archiveTask(
        item: ITask2ReportItem,
        cdr: ChangeDetectorRef,
        actionExecuted$: Subject<ITaskActionExecutedData>,
        isUndo = false,
    ): void {
        const archiveTaskFn = () => {
            this.fiTaskV2Service.simpleArchiveTask([item.Id], true, isUndo).then(() => {
                item.IsArchived = true;
                this.fiTaskSyncService.updateTasksV2ToBeDone([item]);

                if (!isUndo) {
                    this.fiTaskV2HelperService.showTaskArchivedSnackbar(item.Name).subscribe(() => {
                        this.restoreTask(item, cdr, actionExecuted$, true);
                    });
                }

                actionExecuted$.next({ item, type: ACTIONS_TYPES.ARCHIVE });
            });
        };

        if (!isUndo) {
            this.fiTaskV2HelperService.showArchiveTaskConfirmDialog().onYesButtonClick(() => {
                archiveTaskFn();
            });
        } else {
            // skip showing archive confirmation dialog
            archiveTaskFn();
        }
    }

    private restoreTask(
        item: ITask2ReportItem,
        cdr: ChangeDetectorRef,
        actionExecuted$: Subject<ITaskActionExecutedData>,
        isUndo = false,
    ): void {
        this.fiTaskV2Service.simpleArchiveTask([item.Id], false, isUndo).then(() => {
            item.IsArchived = false;
            this.fiTaskSyncService.updateTasksV2ToBeDone([item]);
            if (!isUndo) {
                this.fiTaskV2HelperService
                    .showTaskRestoredSnackbar(item.Name, {
                        tasksRestoredCount: 1,
                        isUndoAvailable: true,
                    })
                    .subscribe(() => {
                        this.archiveTask(item, cdr, actionExecuted$, true);
                    });
            } else {
                this.fiTaskV2HelperService.showTaskRestoredSnackbar(item.Name, {
                    tasksRestoredCount: 1,
                    isUndoMode: true,
                    taskStatus: item.Status,
                });
            }
            actionExecuted$.next({ item, type: ACTIONS_TYPES.RESTORE });
            cdr.markForCheck();
        });
    }

    private updateStatus(
        item: ITask2ReportItem,
        newStatus: FiTaskStatusType,
        cdr: ChangeDetectorRef,
        actionExecuted$: Subject<ITaskActionExecutedData>,
    ): void {
        this.fiTaskV2Service.simpleUpdateTaskStatus([{ Id: item.Id, Status: newStatus }], false).then(() => {
            const prevStatus = item.Status;
            item.Status = newStatus;
            // also we should update report status
            item.ReportStatus = this.fiTaskV2ReportHelperService.getReportStatus(newStatus, item.IsDeadlinePassed);
            this.fiTaskSyncService.updateTasksV2ToBeDone([item]);
            cdr.markForCheck();

            if (newStatus === FiTaskStatusType.Cancelled) {
                this.showTaskStatusChangeWithUndo(item, newStatus, prevStatus, cdr, actionExecuted$);
            } else {
                this.fiTaskV2HelperService.showTaskStatusChangeSnackbarReport(item.Name, newStatus, 'common.dismiss');
            }
            actionExecuted$.next({ item, type: ACTIONS_TYPES.UPDATE_STATUS });
        });
    }

    private showTaskStatusChangeWithUndo(
        item: ITask2ReportItem,
        newStatus: FiTaskStatusType,
        prevStatus: FiTaskStatusType,
        cdr: ChangeDetectorRef,
        actionExecuted$: Subject<ITaskActionExecutedData>,
    ) {
        const snackbar = this.fiTaskV2HelperService
            .showTaskStatusChangeSnackbarReport(item.Name, newStatus)
            .subscribe(() => {
                this.fiTaskV2Service.simpleUpdateTaskStatus([{ Id: item.Id, Status: prevStatus }], true).then(() => {
                    item.Status = prevStatus;
                    item.ReportStatus = this.fiTaskV2ReportHelperService.getReportStatus(
                        newStatus,
                        item.IsDeadlinePassed,
                    );
                    this.fiTaskV2HelperService.showTaskStatusChangeUndoneSnackbarReport(item.Name, prevStatus);
                    cdr.markForCheck();
                    actionExecuted$.next({ item, type: ACTIONS_TYPES.UPDATE_STATUS });
                });
                snackbar.unsubscribe();
            });
    }
}
